<script setup>
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import dayjs from 'dayjs'
import { getI18n } from '@/plugins/i18n'
import PivotGridDataSource from 'devextreme/esm/ui/pivot_grid/data_source'
import { DxPivotGrid, DxScrolling, DxFieldPanel } from 'devextreme-vue/pivot-grid'

import { planningApi } from '@/apis/planning.api'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'
import GridRefreshButton from '@/components/shared/GridRefreshButton.vue'
import StaffMovementsEdit from '@/components/shared/StaffMovementsEdit.vue'
import WarehouseMovementEdit from '@/components/shared/WarehouseMovementEdit.vue'
import { useWebSiteStore } from '@/stores/web-site.store'
import { rangeForPreset } from '@/composables/date-range-presets'
import { formatDayMonthDate } from '@/lib/utils'

const i18n = getI18n()
const router = useRouter()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const localizedServiceTypes = portalStore.getLocalizedServiceTypes()
const { GROUP } = portalStore.serviceTypeEnum

const defaultPeriod = webSiteStore.getDefaultTimelinePeriod() ?? portalStore.dateRangePresetEnum.THIRTY_DAYS
const initialRange = rangeForPreset(defaultPeriod) ?? rangeForPreset(portalStore.dateRangePresetEnum.THIRTY_DAYS)
const dateRange = ref({
  from: initialRange.from.format('YYYY-MM-DD'),
  to: initialRange.to.format('YYYY-MM-DD'),
})

const DATE_COLUMN_WIDTH = 60

const includeScheduled = ref(true)
const showArchivedLocations = ref(false)
const showVirtualLocations = ref(false)
let pivotInstance = null
let scrolledToToday = false
const storeArray = []
const serializedServiceNames = ref(new Set())
const staffServiceNames = ref(new Set())
const singleUnitServiceNames = ref(new Set())
const staffIdByName = ref(new Map())
const equipmentByName = ref(new Map())
const warehouseByName = ref(new Map())
const addAllocation = ref(null)
const addMovement = ref(null)
const showVacationInfo = ref(false)

const pivotFields = [
  {
    caption: i18n.t('common.warehouse'),
    dataField: 'warehouseName',
    area: 'row',
    areaIndex: 0,
    width: 150,
    sortOrder: 'asc',
  },
  {
    caption: i18n.t('common.serviceType'),
    dataField: 'serviceType',
    area: 'row',
    areaIndex: 1,
    width: 120,
    sortOrder: 'asc',
  },
  {
    caption: i18n.t('common.service'),
    dataField: 'serviceName',
    area: 'row',
    areaIndex: 2,
    width: 200,
    sortOrder: 'asc',
  },
  {
    caption: i18n.t('common.date'),
    dataField: 'date',
    area: 'column',
    areaIndex: 0,
    sortOrder: 'asc',
    width: DATE_COLUMN_WIDTH,
    customizeText: (cellInfo) => {
      if (!cellInfo.value) return ''
      return formatDayMonthDate(cellInfo.value)
    },
  },
  {
    caption: i18n.t('common.quantity'),
    dataField: 'quantity',
    area: 'data',
    summaryType: 'sum',
    dataType: 'number',
  },
]

const pivotDataSource = new PivotGridDataSource({
  retrieveFields: false,
  store: storeArray,
  fields: pivotFields,
})

const getServiceTypeLabel = (typeId) => {
  const found = localizedServiceTypes.find(t => t.id === typeId)
  return found ? found.name : ''
}

const buildRecords = (availability, serviceNames, serviceTypes, warehouseNamesById, archivedWarehouseIds) => {
  const records = []
  const from = dayjs(dateRange.value.from)
  const to = dayjs(dateRange.value.to)

  for (const [compositeKey, intervals] of Object.entries(availability)) {
    const [warehouseIdStr, serviceIdStr] = compositeKey.split('_')
    const warehouseId = parseInt(warehouseIdStr)
    if (!showArchivedLocations.value && archivedWarehouseIds.has(warehouseId)) continue
    const serviceId = parseInt(serviceIdStr)
    const serviceName = serviceNames[serviceId] || `Service #${serviceId}`
    const serviceType = getServiceTypeLabel(serviceTypes[serviceId])
    const warehouseName = warehouseNamesById[warehouseId] || `Location #${warehouseId}`

    let day = from
    while (day.isBefore(to) || day.isSame(to, 'day')) {
      const dayStart = day.startOf('day')
      const dayEnd = day.endOf('day')
      let qty = 0
      let found = false
      for (const interval of intervals) {
        const iFrom = dayjs(interval.from)
        const iTo = dayjs(interval.to)
        if (iFrom.isBefore(dayEnd) && iTo.isAfter(dayStart)) {
          qty = found ? Math.min(qty, interval.quantity) : interval.quantity
          found = true
        }
      }
      records.push({
        warehouseName,
        serviceType,
        serviceName,
        date: day.format('YYYY-MM-DD'),
        quantity: qty,
      })
      day = day.add(1, 'day')
    }
  }
  return records
}

const onPivotInitialized = (e) => { pivotInstance = e.component }

const horizontalScrollers = () => {
  const root = pivotInstance?.element?.()
  if (!root) return []
  return [...root.querySelectorAll('.dx-pivotgrid-area-data .dx-scrollable-container')].filter(el => el.scrollWidth - el.clientWidth > 1)
}

// The shared DevExtreme stylesheet forces `overflow: hidden` on the scroll container with
// higher specificity than any scoped rule; an inline style wins over it and re-enables the
// native horizontal scrollbar.
const revealHorizontalScrollbar = (scrollers) => {
  for (const el of scrollers) el.style.overflowX = 'auto'
}

const scrollPivotToToday = (scrollers) => {
  const from = dayjs(dateRange.value.from).startOf('day')
  const to = dayjs(dateRange.value.to).startOf('day')
  const today = dayjs().startOf('day')
  if (today.isBefore(from) || today.isAfter(to)) return
  const offset = today.diff(from, 'day') * DATE_COLUMN_WIDTH
  for (const el of scrollers) el.scrollLeft = offset
  scrolledToToday = true
}

const onPivotContentReady = () => {
  requestAnimationFrame(() => {
    const scrollers = horizontalScrollers()
    if (!scrollers.length) return
    revealHorizontalScrollbar(scrollers)
    if (!scrolledToToday) scrollPivotToToday(scrollers)
  })
}

const loadDataAsync = async () => {
  scrolledToToday = false
  const [equipmentsRes, staffsRes, warehousesRes] = await Promise.all([
    planningStore.fetchEquipmentsAsync({ all: true }),
    planningStore.fetchStaffsAsync({ all: true }),
    planningStore.fetchWarehousesAsync({ all: true, show_archived: true }),
  ])

  const toArray = (v) => Array.isArray(v) ? v : (v?.data?.results ?? v?.data ?? v?.results ?? [])
  const isGroup = (s) => s.type === GROUP
  const equipments = toArray(equipmentsRes).filter(e => !e.archived && !isGroup(e))
  const staffs = toArray(staffsRes).filter(s => !s.archived && !isGroup(s))
  const allServices = [...equipments, ...staffs]

  const serviceIds = allServices.map(s => s.id)
  if (!serviceIds.length) {
    storeArray.splice(0, storeArray.length)
    pivotDataSource.reload()
    return
  }

  // Build warehouse id→name map 
  const allWarehouses = toArray(warehousesRes)
  const warehouseNamesById = {}
  const archivedWarehouseIds = new Set()
  const warehousesByName = new Map()
  for (const w of allWarehouses) {
    warehouseNamesById[w.id] = w.name
    warehousesByName.set(w.name, w)
    if (w.archived) archivedWarehouseIds.add(w.id)
  }
  warehouseByName.value = warehousesByName

  const serviceNames = {}
  const serviceTypes = {}
  const serializedNames = new Set()
  const staffNames = new Set()
  const singleUnitNames = new Set()
  const staffIds = new Map()
  const equipmentsByName = new Map()
  for (const s of allServices) {
    const name = planningStore.getServiceName(s)
    serviceNames[s.id] = name
    serviceTypes[s.id] = s.type
    if (s.is_serialized) serializedNames.add(name)
    if (s.type === portalStore.serviceTypeEnum.STAFF) {
      staffNames.add(name)
      staffIds.set(name, s.id)
    } else if (s.type === portalStore.serviceTypeEnum.EQUIPMENT) {
      equipmentsByName.set(name, s)
    }
    if (Number(s.total_quantity ?? 0) <= 1) singleUnitNames.add(name)
  }
  serializedServiceNames.value = serializedNames
  staffServiceNames.value = staffNames
  singleUnitServiceNames.value = singleUnitNames
  staffIdByName.value = staffIds
  equipmentByName.value = equipmentsByName

  const from = dayjs(dateRange.value.from).startOf('day').toISOString()
  const to = dayjs(dateRange.value.to).endOf('day').toISOString()

  const res = await planningApi.getStockAvailabilityAsync({
    service_ids: serviceIds.join(','),
    date_from: from,
    date_to: to,
    include_scheduled: includeScheduled.value,
    include_virtual: showVirtualLocations.value,
  })

  const availability = res?.data ?? res ?? {}
  const records = buildRecords(availability, serviceNames, serviceTypes, warehouseNamesById, archivedWarehouseIds)
  storeArray.splice(0, storeArray.length, ...records)
  pivotDataSource.reload()
}

const onCellPrepared = ({ cell, cellElement, area }) => {
  if (area !== 'data' || cell.value == null) return
  const qty = cell.value

  // rowPath: [warehouse, type, serviceName]
  const serviceName = cell.rowPath?.length >= 3 ? cell.rowPath[2] : null
  const isSerialized = serviceName && serializedServiceNames.value.has(serviceName)
  const isStaff = serviceName && staffServiceNames.value.has(serviceName)
  const isSingleUnit = serviceName && singleUnitServiceNames.value.has(serviceName)

  if (qty > 1 || (qty === 1 && (isSerialized || isStaff || isSingleUnit))) {
    cellElement.style.backgroundColor = '#c8e6c9'
    cellElement.style.color = '#2e7d32'
  } else if (qty === 1) {
    cellElement.style.backgroundColor = '#fff3e0'
    cellElement.style.color = '#e65100'
  } else {
    cellElement.style.backgroundColor = '#ffcdd2'
    cellElement.style.color = '#c62828'
  }

  cellElement.style.fontWeight = 'bold'
  cellElement.style.textAlign = 'center'

  if (qty < 0) {
    const icon = document.createElement('i')
    icon.className = 'mdi mdi-alert'
    icon.style.marginInlineStart = '.25rem'
    cellElement.appendChild(icon)
  }

  const isEquipment = serviceName && equipmentByName.value.has(serviceName)
  if ((isStaff && qty > 0) || isEquipment) cellElement.style.cursor = 'pointer'
}

const onCellClick = ({ area, cell }) => {
  if (area !== 'data') return
  const serviceName = cell.rowPath?.length >= 3 ? cell.rowPath[2] : null
  const startDate = cell.columnPath?.[0]
  if (!serviceName || !startDate) return

  if (staffServiceNames.value.has(serviceName)) {
    if (!cell.value || cell.value <= 0) return
    const staffId = staffIdByName.value.get(serviceName)
    if (staffId) addAllocation.value = { service: staffId, startDate }
    return
  }

  const equipment = equipmentByName.value.get(serviceName)
  if (equipment) {
    const warehouse = warehouseByName.value.get(cell.rowPath[0]) ?? null
    addMovement.value = { service: equipment, warehouse, startDate }
  }
}

const onAllocationAdded = (payload) => {
  addAllocation.value = null
  loadDataAsync()
  if (payload?.isVacation) showVacationInfo.value = true
}

const goToStaffMovements = () => router.push({ name: 'staff-movements' })

const onMovementAdded = () => {
  addMovement.value = null
  loadDataAsync()
}

watch([dateRange, includeScheduled, showArchivedLocations, showVirtualLocations], loadDataAsync, { immediate: true })
</script>

<template>
  <div class="q-mt-md warehouse-availability-pivot">
    <div class="row q-col-gutter-sm q-mb-sm items-center no-wrap">
      <div class="col-auto">
        <date-range-navigator v-model="dateRange" :default-preset="defaultPeriod" />
      </div>
      <q-space />
      <div class="col-auto">
        <q-toggle
          v-model="includeScheduled"
          :label="$t('common.warehouse.includeScheduled', 2)"
          color="primary"
          dense
        />
      </div>
      <div class="col-auto">
        <q-toggle
          v-model="showArchivedLocations"
          :label="$t('common.showArchived')"
          color="primary"
          dense
        />
      </div>
      <div class="col-auto">
        <q-toggle
          v-model="showVirtualLocations"
          :label="$t('common.showVirtualLocations')"
          color="primary"
          dense
        />
      </div>
      <div class="col-auto row items-center">
        <div class="toolbar-separator" />
      </div>
      <div class="col-auto">
        <grid-refresh-button :action="loadDataAsync" />
      </div>
    </div>

    <dx-pivot-grid
      :data-source="pivotDataSource"
      :allow-sorting-by-summary="false"
      :allow-filtering="true"
      :show-borders="true"
      :show-column-grand-totals="false"
      :show-row-grand-totals="false"
      :show-column-totals="false"
      :show-row-totals="false"
      height="calc(100vh - 15rem)"
      :word-wrap-enabled="false"
      @initialized="onPivotInitialized"
      @content-ready="onPivotContentReady"
      @cell-prepared="onCellPrepared"
      @cell-click="onCellClick"
    >
      <dx-field-panel
        :show-column-fields="true"
        :show-row-fields="true"
        :show-data-fields="false"
        :show-filter-fields="true"
        :allow-field-dragging="true"
        :visible="true"
      />
      <dx-scrolling mode="virtual" :use-native="true" />
    </dx-pivot-grid>

    <q-dialog :model-value="addAllocation !== null" class="dialog-md" @hide="addAllocation = null">
      <staff-movements-edit
        v-if="addAllocation !== null"
        :service="addAllocation.service"
        :start-date="addAllocation.startDate"
        @update="onAllocationAdded"
        @cancel="addAllocation = null"
      />
    </q-dialog>

    <q-dialog :model-value="addMovement !== null" class="dialog-md" @hide="addMovement = null">
      <warehouse-movement-edit
        v-if="addMovement !== null"
        :model-value="{}"
        :service="addMovement.service"
        :warehouse="addMovement.warehouse"
        :scheduled-date="addMovement.startDate"
        @update="onMovementAdded"
        @cancel="addMovement = null"
        @delete="onMovementAdded"
      />
    </q-dialog>

    <confirm-dialog
      v-model="showVacationInfo"
      :title="i18n.t('warning.vacationPendingApprovalTitle')"
      :message="i18n.t('warning.vacationNotCountedUntilApproved')"
      icon="mdi-information"
      icon-color="info"
      :label="i18n.t('common.goToStaffMovements')"
      :cancel-label="i18n.t('common.close')"
      @confirm="goToStaffMovements"
    />
  </div>
</template>

<style scoped>
/* DevExtreme's native scroller relies on the OS scrollbar, which is an auto-hiding overlay
   on macOS. Force a classic, always-visible horizontal scrollbar on the data area so the
   date columns are visibly scrollable. `-webkit-appearance: none` is what opts out of the
   overlay bar on WebKit/Blink. */
.warehouse-availability-pivot :deep(.dx-pivotgrid-area-data .dx-scrollable-container) {
  overflow-x: auto !important;
}
.warehouse-availability-pivot :deep(.dx-pivotgrid-area-data .dx-scrollable-container)::-webkit-scrollbar:horizontal {
  -webkit-appearance: none;
  height: 0.75rem;
}
.warehouse-availability-pivot :deep(.dx-pivotgrid-area-data .dx-scrollable-container)::-webkit-scrollbar-thumb:horizontal {
  background: rgba(128, 128, 128, 0.6);
  border-radius: 0.375rem;
}
</style>
